import { useCallback, useEffect, useState } from "react";
import { FeatureHintAktion } from "./FeatureHintAnsicht";
import useApiRequest from "../../lib/hooks/useRequest";

/** Was eine Person mit einer Neuigkeit getan hat (KAN-296). */
export interface FeatureHintEreignis {
  key: string;
  art: "gesehen" | "geoeffnet" | "knopf" | "video";
  /** Welcher Knopf, welches Video. Bei gesehen und geoeffnet weggelassen. */
  stelle?: number;
}

export interface FeatureHintVideo {
  provider: "youtube" | "vimeo" | "loom";
  embedUrl: string;
  title: string;
  providerTitle: string | null;
  duration: string | null;
  thumbnailUrl: string | null;
}

export type FeatureHintGroup = "neu" | "verbessert" | "behoben" | "erinnerung" | "wissenswert";

/**
 * Die Umfrage einer Neuigkeit (KAN-302).
 *
 * Hier steht bewusst kein Ergebnis: Was andere geantwortet haben, sieht nur der
 * Super-Admin. `meineAntwort` ist die eigene Stimme - leer heisst noch nicht abgestimmt.
 */
export interface FeatureHintUmfrage {
  frage: string;
  optionen: { stelle: number; label: string }[];
  mehrfach: boolean;
  /** Ob unter der Frage steht, dass die Antwort beim kandidat.io-Team ankommt. */
  nichtAnonym: boolean;
  meineAntwort: number[];
}

export interface FeatureHintListItem {
  key: string;
  group: FeatureHintGroup;
  date: string;
  title: string;
  /** Der Anriss in der Liste - derselbe Text wie `solutionText`, nur gekuerzt dargestellt. */
  text: string;
  problemText: string;
  solutionText: string;
  ctaLabel: string;
  ctaPath: string;
  /** Hoechstens zwei Knoepfe, schon aufgeloest (KAN-276, A7). */
  actions: FeatureHintAktion[];
  /** Bild ueber der Neuigkeit, 16:9 (KAN-276, A10) - oder null, wenn keines haengt. */
  imageUrl: string | null;
  /** Bestand: das erste Video. Neu gelesen wird `videos` (KAN-291). */
  video: FeatureHintVideo | null;
  /** Alle Videos der Neuigkeit, in Reihenfolge (KAN-291). */
  videos: FeatureHintVideo[];
  /** Die Umfrage, wenn der Eintrag eine hat (KAN-302). */
  umfrage: FeatureHintUmfrage | null;
  seen: boolean;
}

/**
 * Die Datengrundlage des Neuigkeiten-Bereichs (KAN-251). Eintrag und Bereich teilen sich
 * diesen Hook, damit das Abzeichen in der Seitenleiste und die Liste im Bereich immer
 * denselben Stand zeigen.
 *
 * Waehrend einer Super-Admin-Impersonierung liefert das Backend eine leere Liste und die
 * Markier-Aufrufe aendern nichts (A7) - dieser Hook braucht dafuer keine eigene Logik.
 */
export const useFeatureHints = () => {
  const { apiRequest } = useApiRequest();
  const [hints, setHints] = useState<FeatureHintListItem[]>([]);
  // Waehrend einer Super-Admin-Impersonierung darf nichts markiert werden. Das Kennzeichen
  // kommt vom Server, damit die Oberflaeche nicht vorgibt, etwas gelesen zu haben, waehrend
  // der Server den Stand des Kunden unangetastet laesst.
  const [readOnly, setReadOnly] = useState(false);

  const load = useCallback(async () => {
    const response = await apiRequest<{ hints: FeatureHintListItem[]; readOnly: boolean }>("feature-hints", "GET");
    const geliefert = response.data;
    if (!geliefert || !Array.isArray(geliefert.hints)) return;
    setHints(geliefert.hints);
    setReadOnly(!!geliefert.readOnly);
  }, [apiRequest]);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = useCallback(
    (key: string) => {
      if (readOnly) return;
      setHints(prev => prev.map(hint => (hint.key === key ? { ...hint, seen: true } : hint)));
      apiRequest(`feature-hints/${key}/read`, "POST");
    },
    [apiRequest, readOnly]
  );

  const markAllRead = useCallback(() => {
    if (readOnly) return;
    setHints(prev => prev.map(hint => ({ ...hint, seen: true })));
    apiRequest("feature-hints/mark-all-read", "POST");
  }, [apiRequest, readOnly]);

  /**
   * Meldet, was gerade passiert ist (KAN-296).
   *
   * Gebuendelt in einem Aufruf: Beim Zeichnen einer Liste faellt fuer jeden sichtbaren
   * Eintrag eine Meldung an, und ein Aufruf je Zeile waere ein Sturm fuer eine Zahl, die
   * niemand sofort braucht.
   *
   * Waehrend einer Impersonierung schweigt die Oberflaeche schon hier, damit interne Blicke
   * gar nicht erst unterwegs sind - der Server weist sie ohnehin ab.
   *
   * Nichts davon wartet und nichts davon meldet einen Fehler: Eine Messung darf den
   * Bildschirm des Kunden nie unterbrechen.
   */
  const melden = useCallback(
    (ereignisse: FeatureHintEreignis[]) => {
      if (readOnly || ereignisse.length === 0) return;
      apiRequest("feature-hints/ereignisse", "POST", { body: { ereignisse }, withoutErrorModal: true });
    },
    [apiRequest, readOnly]
  );

  /**
   * Die Antwort auf eine Umfrage (KAN-302).
   *
   * Anders als eine Messung darf das nicht still verloren gehen: Eine Stimme ist eine
   * Aussage, und wer sie abgibt, erwartet, dass sie ankommt. Die Oberflaeche zeigt sie
   * sofort und nimmt sie zurueck, wenn der Server sie nicht annimmt - die Rueckgabe sagt,
   * ob es geklappt hat.
   *
   * Waehrend einer Impersonierung passiert nichts: Eine interne Stimme im Namen eines
   * Kunden stuende unsichtbar in seinen Zahlen.
   */
  const umfrageBeantworten = useCallback(
    async (key: string, optionen: number[]): Promise<boolean> => {
      if (readOnly) return false;

      const vorher = hints.find(hint => hint.key === key)?.umfrage?.meineAntwort ?? [];
      const setzen = (antwort: number[]): void =>
        setHints(prev => prev.map(hint => (hint.key === key && hint.umfrage ? { ...hint, umfrage: { ...hint.umfrage, meineAntwort: antwort } } : hint)));

      setzen(optionen);

      const response = await apiRequest(`feature-hints/${key}/umfrage`, "POST", { body: { optionen }, withoutErrorModal: true });
      const geklappt = response.status >= 200 && response.status < 300;
      if (!geklappt) setzen(vorher);

      return geklappt;
    },
    [apiRequest, hints, readOnly]
  );

  const unreadCount = hints.filter(hint => !hint.seen).length;

  return { hints, unreadCount, readOnly, markRead, markAllRead, melden, umfrageBeantworten };
};
